import { useMemo, useRef } from 'react';
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  I18nProvider,
  Popover,
} from 'react-aria-components';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseDate, type DateValue } from '@internationalized/date';
import { cn } from '@/lib/utils';

type DatePickerInputProps = {
  value?: string;
  onChange: (_value: string) => void;
  minValue?: DateValue;
  isDisabled?: boolean;
  locale?: string;
  ariaLabel?: string;
  className?: string;
};

const parseDateValue = (value?: string) => {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
};

export function DatePickerInput({
  value,
  onChange,
  minValue,
  isDisabled,
  locale = 'en-GB',
  ariaLabel,
  className,
}: DatePickerInputProps) {
  const parsedValue = useMemo(() => parseDateValue(value), [value]);
  const popoverContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <I18nProvider locale={locale}>
      <div ref={popoverContainerRef} className={cn('w-full', className)}>
        <DatePicker
          aria-label={ariaLabel}
          value={parsedValue ?? undefined}
          onChange={(next) => onChange(next ? next.toString() : '')}
          minValue={minValue}
          isDisabled={isDisabled}
          className="w-full"
        >
          <Group className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset disabled:cursor-not-allowed disabled:opacity-50">
            <DateInput className="flex flex-1 flex-wrap gap-1">
              {(segment) => (
                <DateSegment
                  segment={segment}
                  className="rounded px-0.5 text-sm outline-none data-[type=literal]:px-0 data-[placeholder]:text-muted-foreground data-[invalid]:text-destructive data-[focused]:bg-accent data-[focused]:text-accent-foreground"
                />
              )}
            </DateInput>
            <AriaButton className="ml-2 rounded-sm text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset">
              <CalendarIcon className="h-4 w-4" />
            </AriaButton>
          </Group>

          <Popover
            UNSTABLE_portalContainer={popoverContainerRef.current ?? undefined}
            className="z-50 rounded-md border bg-background p-3 shadow-md"
          >
            <Dialog className="outline-none">
              <Calendar className="space-y-2">
                <header className="flex items-center justify-between">
                  <AriaButton
                    slot="previous"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </AriaButton>
                  <Heading className="text-sm font-medium" />
                  <AriaButton
                    slot="next"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </AriaButton>
                </header>

                <CalendarGrid className="text-xs">
                  <CalendarGridHeader>
                    {(day) => (
                      <CalendarHeaderCell className="h-8 w-8 text-center text-[10px] font-medium text-muted-foreground">
                        {day}
                      </CalendarHeaderCell>
                    )}
                  </CalendarGridHeader>
                  <CalendarGridBody>
                    {(date) => (
                      <CalendarCell
                        date={date}
                        className="h-8 w-8 rounded-md text-center text-sm leading-8 text-foreground transition hover:bg-accent hover:text-accent-foreground data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[outside-month]:text-muted-foreground data-[outside-month]:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                      />
                    )}
                  </CalendarGridBody>
                </CalendarGrid>
              </Calendar>
            </Dialog>
          </Popover>
        </DatePicker>
      </div>
    </I18nProvider>
  );
}
